import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiEnvelope<T> {
  error: boolean;
  data?: T;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class ApiClientService {
  private readonly http = inject(HttpClient);
  readonly baseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  get<T>(path: string, params?: Record<string, string | number | boolean | undefined | null>): Observable<T> {
    let httpParams = new HttpParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') {
          httpParams = httpParams.set(k, String(v));
        }
      }
    }
    return this.http
      .get<ApiEnvelope<T>>(`${this.baseUrl}${path}`, { params: httpParams })
      .pipe(
        map((body) => this.unwrap(body)),
        catchError((err) => this.toApiError(err)),
      );
  }

  post<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .post<ApiEnvelope<T>>(`${this.baseUrl}${path}`, body ?? {})
      .pipe(
        map((b) => this.unwrap(b)),
        catchError((err) => this.toApiError(err)),
      );
  }

  patch<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .patch<ApiEnvelope<T>>(`${this.baseUrl}${path}`, body ?? {})
      .pipe(
        map((b) => this.unwrap(b)),
        catchError((err) => this.toApiError(err)),
      );
  }

  put<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .put<ApiEnvelope<T>>(`${this.baseUrl}${path}`, body ?? {})
      .pipe(
        map((b) => this.unwrap(b)),
        catchError((err) => this.toApiError(err)),
      );
  }

  delete<T>(path: string): Observable<T> {
    return this.http
      .delete<ApiEnvelope<T>>(`${this.baseUrl}${path}`)
      .pipe(
        map((b) => this.unwrap(b)),
        catchError((err) => this.toApiError(err)),
      );
  }

  /** Authenticated binary download (attachments). */
  getBlob(path: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${path}`, { responseType: 'blob' }).pipe(
      catchError((err) => this.toApiError(err)),
    );
  }

  private unwrap<T>(body: ApiEnvelope<T>): T {
    if (body?.error) {
      throw new Error(body.message || 'API request failed');
    }
    return body.data as T;
  }

  private toApiError(err: unknown) {
    if (err instanceof HttpErrorResponse) {
      const msg =
        (typeof err.error === 'object' && err.error && (err.error as ApiEnvelope<unknown>).message) ||
        err.message ||
        'API request failed';
      return throwError(() => new Error(String(msg)));
    }
    if (err instanceof Error) {
      return throwError(() => err);
    }
    return throwError(() => new Error('API request failed'));
  }

  static authHeaders(token: string | null): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
}
